/**
 * Cart-page surcharge calculation.
 *
 * The surcharge (handling + other + freight + expedite fees) is computed by the Gadget
 * order-simulate API and persisted to the cart's `_surcharges` attribute. Historically this
 * only ran on the review-order page, so on the first forward navigation the attribute was
 * still blank and the surcharge row was missing on the cart and checkout pages (it only
 * appeared once the customer returned to the cart via the breadcrumb). This module runs the
 * same order-simulate call on the cart page so the attribute is persisted on first visit.
 *
 * Display logic is unchanged: the checkout and review-order pages still read `_surcharges`
 * and apply the $500 threshold at display time. We persist the raw computed value here and
 * let each page's existing display logic gate it.
 */

import { getCtx } from '@theme/rs-current-company-location';
import { getCustomerAppBaseUrl } from '@theme/gadget-envs';

const SURCHARGE_CENTS_SELECTOR = '[data-surcharges-cents]';

// Surcharge only applies to orders under this amount (dollars); must match the threshold in
// b2b-checkout.js and the server-side draft order creation. order-simulate returns the raw fee
// regardless of order size, so the cart page has to gate it itself.
const SURCHARGE_THRESHOLD = 500;

// Poll for RTP's material list on first load. The list is the source of truth for B2B unit
// prices (base prices are $0), so we cannot build an accurate order total before it lands.
// The material list is populated by the first `realtimePricing()` pass; we resolve as soon
// as it appears, then fall back to `cart:rtp-pricing-ready` for all subsequent changes.
const RTP_READY_POLL_MS = 250;
const RTP_READY_TIMEOUT_MS = 20000;
// Coalesce triggers (ready-signal + polling) so a single cart state yields one API call.
const RECOMPUTE_DEBOUNCE_MS = 250;

/**
 * Parse date string to YYYY-MM-DD (mirrors order-simulate.js).
 * @param {string} dateStr
 * @returns {string|null}
 */
function toApiDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Parse split deliveries from Shopify's JSON string/array property.
 * Supports verbose `{quantity,_delivery_date}` and compact `{q,d}` entries.
 * @param {unknown} value
 * @returns {Array<{quantity: number, deliveryDate: string}>}
 */
function parseRequestedDeliveries(value) {
  let deliveries = value;
  if (typeof deliveries === 'string') {
    try {
      deliveries = JSON.parse(deliveries);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(deliveries)) return [];

  return deliveries
    .flat(Infinity)
    .map((delivery) => {
      if (!delivery || typeof delivery !== 'object' || Array.isArray(delivery)) return null;
      const quantity = Number(delivery.quantity ?? delivery.q);
      const deliveryDate = toApiDate(delivery._delivery_date ?? delivery.d ?? delivery.date ?? delivery.availableDate);
      if (!Number.isFinite(quantity) || quantity <= 0 || !deliveryDate) return null;
      return { quantity, deliveryDate };
    })
    .filter((delivery) => delivery != null);
}

/**
 * Resolve a cart line's unit price in cents, mirroring order-simulate.js:
 * _price_override property first, then the RTP material list (by SKU or alias).
 * @param {Object} item - Cart line from /cart.js
 * @returns {number|null} unit price in cents, or null if unresolved
 */
function getRtpUnitCents(item) {
  const override = item?.properties?._price_override;
  if (override != null && override !== '') {
    const parsed = Number(override);
    if (Number.isFinite(parsed) && parsed > 0) return Math.round(parsed * 100);
  }
  const sku = (item?.properties?._sku || item?.sku || '').trim();
  const list = window.RTP?.lastCartMaterialList;
  const aliases = window.RTP?.pricingSkuAliases;
  if (!Array.isArray(list) || !sku) return null;
  const match = list.find((m) => {
    const matNum = String(m.materialNumber ?? '').trim();
    if (matNum === sku) return true;
    const raws = aliases?.get(matNum);
    return raws ? raws.some((r) => String(r).trim() === sku) : false;
  });
  if (!match || match.netPrice == null) return null;
  return Math.round(Number(match.netPrice) * 100);
}

/**
 * Compute the order total in dollars from RTP-resolved line prices.
 * B2B variants have $0 Shopify base prices, so cart.total_price is always 0 and cannot be
 * used; fall back to it only when nothing resolves via RTP.
 * @param {Object} cart - Shopify cart from /cart.js
 * @returns {number} total in dollars
 */
function buildTotalOrderAmount(cart) {
  const items = cart?.items || [];
  let cents = 0;
  let resolved = false;
  for (const item of items) {
    const unit = getRtpUnitCents(item);
    if (unit != null) {
      cents += unit * (item.quantity || 0);
      resolved = true;
    }
  }
  if (!resolved) cents = Math.round(cart?.total_price || 0);
  return cents / 100;
}

/**
 * Build the order-simulate request from cart data alone.
 *
 * Unlike order-simulate.js (review-order page), there is no selected shipping address or PO
 * number yet, so we omit `customShippingAddress`/`attention` — the backend falls back to the
 * company location's default address (see POST-order.ts) — and leave the PO number blank.
 * The bill-to account is left out so the backend derives it from the company metafield.
 * @param {Object} cart - Shopify cart from /cart.js
 * @returns {Object}
 */
function buildOrderRequest(cart) {
  const ctx = getCtx();
  const attributes = cart?.attributes || {};

  const companyGid = document.body?.dataset?.companyId
    ? 'gid://shopify/Company/' + document.body.dataset.companyId
    : '';
  const companyLocationGid = document.body?.dataset?.locationId
    ? 'gid://shopify/CompanyLocation/' + document.body.dataset.locationId
    : '';
  const storeName = (window.Shopify?.shop || 'dev-customer-resideo').replace('.myshopify.com', '') || 'dev-customer-resideo';
  const companyExternalId = ctx?.company_external_id || ctx?.location_external_id || document.body?.dataset?.soldTo || '';

  const shipComplete = attributes.ship_complete === 'true';
  const shippingCondition = attributes._shipping_condition || '01';
  const carrierExternalId = attributes._carrier_external_id || '0';
  const freightTerm = attributes._freight_term || '001';

  const lineItems = (cart.items || [])
    .flatMap((item) => {
      const requestedDelivery = item.properties?._requested_delivery || item.properties?._requested_deliveries || item.properties?._delivery_date;
      const lineShipComplete = item.properties?._ship_complete === 'true';
      const sku = item.properties?._sku || '';
      const baseLineItem = {
        itemId: String(item.variant_id || item.id),
        sku: sku || undefined,
        shipCompleteLineLevelOrder: lineShipComplete
      };

      const splitDeliveries = parseRequestedDeliveries(requestedDelivery);
      if (splitDeliveries.length > 0) {
        return splitDeliveries.map((delivery) => ({
          ...baseLineItem,
          quantity: delivery.quantity,
          requestedDeliveryDate: delivery.deliveryDate
        }));
      }

      return [{
        ...baseLineItem,
        quantity: item.quantity,
        requestedDeliveryDate: toApiDate(requestedDelivery)
      }];
    })
    .filter((li) => li.itemId && li.quantity > 0);

  return {
    companyGid: companyGid || '',
    companyLocationGid: companyLocationGid || '',
    storeName,
    salesArea: document.body?.dataset?.currentSalesArea || '',
    header: {
      purchaseOrderNumber: '',
      customerEmail: document.body?.dataset?.customerEmail || '',
      companyExternalId: companyExternalId || '',
      shipToId: document.body?.dataset?.shipTo || '',
      attention: '',
      shipCompleteOrder: shipComplete,
      shippingCondition: shippingCondition || '01',
      carrierExternalId: carrierExternalId || undefined,
      freightTerm: freightTerm || undefined,
      promoCode: attributes._promo_code || '',
      totalOrderAmount: buildTotalOrderAmount(cart),
      currencyCode: cart.currency || window.Shopify?.currency?.active || 'USD'
    },
    lineItems: lineItems
  };
}

/**
 * Call the order-simulate API (mirrors order-simulate.js).
 * @param {string} apiUrl
 * @param {Object} body
 * @returns {Promise<Response>}
 */
function callOrderSimulate(apiUrl, body) {
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  };
  if (window.myJWT) {
    headers.Authorization = `Bearer ${window.myJWT}`;
  }
  return fetch(apiUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  });
}

/**
 * Persist the surcharge to the cart, reusing the serialized cart-write path when available so
 * we don't clobber a concurrent read-modify-write. `/cart/update.js` merges attributes, so
 * writing only `_surcharges` is safe.
 * @param {string} value - surcharge in dollars, e.g. "12.50"
 * @returns {Promise<void>}
 */
async function persistSurcharge(value) {
  const mutations = /** @type {any} */ (window).cartMutationsInstance;
  if (mutations && typeof mutations.queueCartWrite === 'function' && typeof mutations.writeCartAttributes === 'function') {
    await mutations.queueCartWrite(() => mutations.writeCartAttributes({ _surcharges: value }));
  } else {
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { _surcharges: value } })
    });
  }
}

/**
 * Format a dollar amount in the cart's currency, matching the theme's narrow-symbol money
 * format used by checkout/review-order.
 * @param {number} dollars
 * @param {string} currencyCode
 * @returns {string}
 */
function formatSurchargeMoney(dollars, currencyCode) {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(dollars);
  } catch {
    return dollars.toFixed(2);
  }
}

const SURCHARGE_LOADING_HTML =
  '<span class="rs-modal__loading-ellipsis" aria-hidden="true"><span></span></span>';

/**
 * Show/hide a loading indicator in the surcharge row while a recompute is in flight. The row is
 * revealed (it may be hidden at $0) and the prior content is restored if the recompute bails out
 * without applying a value (error / early return).
 * @param {boolean} loading
 */
function setSurchargeLoading(loading) {
  document.querySelectorAll(SURCHARGE_CENTS_SELECTOR).forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    const row = el.closest('.b2b-checkout__summary-row');
    if (loading) {
      if (el.dataset.surchargeLoading === 'true') return;
      el.dataset.surchargeLoading = 'true';
      el.dataset.surchargePrevHtml = el.innerHTML;
      el.dataset.surchargePrevDisplay = row instanceof HTMLElement ? row.style.display : '';
      el.classList.remove('b2b-checkout__summary-value--free');
      el.innerHTML = SURCHARGE_LOADING_HTML;
      el.setAttribute('aria-busy', 'true');
      if (row instanceof HTMLElement) row.style.display = '';
    } else {
      if (el.dataset.surchargeLoading !== 'true') return;
      // Restore only if nothing applied a fresh value meanwhile (apply clears the flag).
      el.innerHTML = el.dataset.surchargePrevHtml || '';
      if (row instanceof HTMLElement) row.style.display = el.dataset.surchargePrevDisplay || '';
      el.classList.toggle('b2b-checkout__summary-value--free', parseInt(el.dataset.surchargesCents || '0', 10) === 0);
      el.removeAttribute('aria-busy');
      delete el.dataset.surchargeLoading;
      delete el.dataset.surchargePrevHtml;
      delete el.dataset.surchargePrevDisplay;
    }
  });
}

/**
 * Reflect a computed surcharge in the cart summary DOM: update the cents hook (which feeds the
 * estimated total via RTP's `readSurchargeCentsFromDom`), set the visible amount, and toggle
 * row visibility. Then ask RTP to re-render the estimated total.
 * @param {number} surchargesCents
 */
function applySurchargeToDom(surchargesCents) {
  const elements = Array.from(document.querySelectorAll(SURCHARGE_CENTS_SELECTOR));
  if (!elements.length) return;

  const currencyCode = elements[0].dataset.currency || window.Shopify?.currency?.active || 'USD';

  elements.forEach((el) => {
    el.removeAttribute('aria-busy');
    delete el.dataset.surchargeLoading;
    delete el.dataset.surchargePrevHtml;
    delete el.dataset.surchargePrevDisplay;
    el.dataset.surchargesCents = String(surchargesCents);
    const row = el.closest('.b2b-checkout__summary-row');
    if (surchargesCents === 0) {
      el.classList.add('b2b-checkout__summary-value--free');
      if (row) row.style.display = 'none';
    } else {
      el.classList.remove('b2b-checkout__summary-value--free');
      el.textContent = formatSurchargeMoney(surchargesCents / 100, currencyCode);
      if (row) row.style.display = '';
    }
  });

  window.RTP?.refreshEstimatedCartTotals?.();
}

/**
 * Compute the surcharge for the current cart and persist + display it.
 * @param {Object} cart - Shopify cart from /cart.js
 */
async function computeSurcharge(cart) {
  if (!cart?.items || cart.items.length === 0) {
    applySurchargeToDom(0);
    return;
  }

  const apiUrl = `${getCustomerAppBaseUrl()}/api/checkout/order`;
  const body = buildOrderRequest(cart);

  let response;
  try {
    response = await callOrderSimulate(apiUrl, body);
  } catch (err) {
    // Network error: leave the surcharge row as-is (hidden). The authoritative computation
    // still runs at checkout, so nothing is lost.
    console.error('[cart-surcharge] order-simulate request failed', err);
    return;
  }

  if (!response.ok) {
    console.error('[cart-surcharge] order-simulate request failed with status', response.status);
    return;
  }

  let responseData;
  try {
    responseData = await response.json();
  } catch {
    console.error('[cart-surcharge] failed to parse order-simulate response');
    return;
  }

  if (responseData?.orderResponse?.isSuccess === false) {
    console.error('[cart-surcharge] order simulate failed:', responseData.orderResponse?.errors);
    return;
  }

  const header = responseData?.orderResponse?.header;
  const surcharges =
    (parseFloat(header?.handlingFee) || 0) +
    (parseFloat(header?.otherCharges) || 0) +
    (parseFloat(header?.freightFee) || 0) +
    (parseFloat(header?.expediteFee) || 0);

  if (isNaN(surcharges) || surcharges < 0) {
    console.error('[cart-surcharge] invalid surcharge value', surcharges);
    return;
  }

  // Persist the raw value (other pages gate it at display time), but always write it so a
  // stale value from an earlier, smaller cart is never left behind.
  const currentRaw = parseFloat(cart.attributes?._surcharges);
  if (!(Number.isFinite(currentRaw) && currentRaw === surcharges)) {
    await persistSurcharge(surcharges.toFixed(2));
  }

  const orderTotal = body.header.totalOrderAmount;
  const applies = orderTotal < SURCHARGE_THRESHOLD;
  applySurchargeToDom(applies ? Math.round(surcharges * 100) : 0);
}

let recomputeTimer = null;
let running = false;
let pending = false;

/**
 * One recompute pass, guarded so overlapping triggers (ready-signal + first-load poll) coalesce.
 * @param {Object|null} [cart] - optional pre-fetched cart
 */
async function runRecompute(cart) {
  if (running) {
    pending = true;
    return;
  }
  running = true;
  try {
    const data = cart || (await fetch('/cart.js').then((r) => r.json()));
    await computeSurcharge(data);
  } catch (err) {
    console.error('[cart-surcharge] recompute failed', err);
  } finally {
    running = false;
    if (pending) {
      pending = false;
      scheduleRecompute();
    } else if (!recomputeTimer) {
      setSurchargeLoading(false);
    }
  }
}

function scheduleRecompute() {
  setSurchargeLoading(true);
  clearTimeout(recomputeTimer);
  recomputeTimer = setTimeout(() => {
    recomputeTimer = null;
    runRecompute();
  }, RECOMPUTE_DEBOUNCE_MS);
}

/**
 * Wait until RTP's material list is available (needed for an accurate order total), resolving
 * once it appears or the timeout elapses.
 * @returns {Promise<boolean>} true if the material list is available
 */
function waitForRtpMaterialList() {
  return new Promise((resolve) => {
    const check = () => {
      const list = window.RTP?.lastCartMaterialList;
      if (Array.isArray(list)) return resolve(true);
      return false;
    };
    if (check()) return;

    const started = Date.now();
    const interval = setInterval(() => {
      if (check()) {
        clearInterval(interval);
        clearTimeout(timeout);
        resolve(true);
        return;
      }
      if (Date.now() - started > RTP_READY_TIMEOUT_MS) {
        clearInterval(interval);
        clearTimeout(timeout);
        resolve(false);
      }
    }, RTP_READY_POLL_MS);
    const timeout = setTimeout(() => clearInterval(interval), RTP_READY_TIMEOUT_MS);
  });
}

async function init() {
  // Only the table-layout cart summary carries the surcharge row; stay inert otherwise.
  if (!document.querySelector(SURCHARGE_CENTS_SELECTOR)) return;

  // Re-run on every successful cart-page pricing pass. This covers quantity edits, which
  // re-run realtime pricing and fire `cart:rtp-pricing-ready` (it only fires when the checkout
  // link is present, i.e. the table layout with the surcharge row).
  document.addEventListener('cart:rtp-pricing-ready', scheduleRecompute);

  // Quantity edits only refresh the edited line (no full pricing pass), so recompute on the
  // cart-updates.js signal fired after `/cart/change.js` settles.
  document.addEventListener('cart:line-quantity-updated', scheduleRecompute);
  // Show the loading state as soon as the edit starts, not only once the cart write settles.
  document.addEventListener('cart:line-quantity-changing', () => setSurchargeLoading(true));

  // First load: the ready-signal may fire after this listener attaches, so poll for the
  // material list and recompute once it's ready. Subsequent changes arrive via the event above.
  const ready = await waitForRtpMaterialList();
  if (ready) scheduleRecompute();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
